/**
 * Microsoft sign-in through MSAL.js's popup flow (`@azure/msal-browser`).
 *
 * MSAL is loaded with a dynamic `import()` the first time someone presses the
 * button, never in the initial bundle. Once loaded and initialised it is kept,
 * so a second press calls `loginPopup` synchronously inside the click — the
 * surest way past a popup blocker that already refused the first, slower one.
 *
 * The popup returns to `window.location.origin` (the only redirect URIs the
 * app registration holds). MSAL v5 does not read the popup's URL itself: the
 * page loaded there must hand the response back over a BroadcastChannel — see
 * `finishMicrosoftSignInPopup`, which the app served at the origin root runs
 * before anything else.
 */
import type * as MsalModule from "@azure/msal-browser";
import type { PublicClientApplication } from "@azure/msal-browser";

const AUTHORITY = "https://login.microsoftonline.com/common";
const SCOPES = ["openid", "profile", "email"];
/** How long the opener waits for the popup's answer (MSAL's default is 60s). */
const POPUP_TIMEOUT_MS = 10 * 60_000;

type Msal = typeof MsalModule;

let loading: Promise<{ msal: Msal; app: PublicClientApplication }> | null = null;
let ready: { clientId: string; msal: Msal; app: PublicClientApplication } | null = null;

function load(clientId: string): Promise<{ msal: Msal; app: PublicClientApplication }> {
  loading ??= import("@azure/msal-browser")
    .then(async (msal) => {
      const app = new msal.PublicClientApplication({
        auth: { clientId, authority: AUTHORITY, redirectUri: window.location.origin },
        // Nothing of Microsoft's is kept: the ID token goes straight to our
        // backend, which mints our own session.
        cache: { cacheLocation: msal.BrowserCacheLocation.MemoryStorage },
        system: { popupBridgeTimeout: POPUP_TIMEOUT_MS },
      });
      await app.initialize();
      ready = { clientId, msal, app };
      return { msal, app };
    })
    .catch((err: unknown) => {
      loading = null; // let a later press try again
      throw err;
    });
  return loading;
}

/**
 * Opens Microsoft's account picker and resolves with the ID token. The popup
 * being closed does not settle the promise until MSAL's timeout, so
 * `onPopupClosed` fires as soon as it is seen closed and the caller resets
 * then. Rejects when the popup is blocked or Microsoft answers with an error.
 */
export function signInWithMicrosoftPopup(
  clientId: string,
  onPopupClosed: () => void,
): Promise<string> {
  const run = ({ msal, app }: { msal: Msal; app: PublicClientApplication }): Promise<string> => {
    let watch: number | undefined;
    const callbackId = app.addEventCallback(
      (message) => {
        const popup = (message.payload as { popupWindow?: Window } | null)?.popupWindow;
        if (popup === undefined) return;
        // Known limit: `closed` polling. Microsoft's pages send COOP report-only today;
        // once enforced the handle may read closed early — the caller still
        // accepts a late token, so that only re-enables the button sooner.
        watch = window.setInterval(() => {
          if (popup.closed) {
            window.clearInterval(watch);
            onPopupClosed();
          }
        }, 500);
      },
      [msal.EventType.POPUP_OPENED],
    );
    return app
      .loginPopup({
        scopes: SCOPES,
        prompt: "select_account",
        // A popup the person closed leaves MSAL "in progress" until its
        // timeout; a new press replaces that attempt instead of failing.
        overrideInteractionInProgress: true,
      })
      .then((result) => {
        if (result.idToken === "") throw new Error("Microsoft returned no ID token");
        return result.idToken;
      })
      .finally(() => {
        window.clearInterval(watch);
        if (callbackId !== null) app.removeEventCallback(callbackId);
      });
  };
  return ready !== null && ready.clientId === clientId ? run(ready) : load(clientId).then(run);
}

/** MSAL error codes that mean "nobody finished signing in" — reset quietly. */
export function isMicrosoftPopupCancel(err: unknown): boolean {
  const code = (err as { errorCode?: unknown } | null)?.errorCode;
  return (
    code === "user_cancelled" ||
    code === "popup_window_error" ||
    code === "empty_window_error" ||
    code === "timed_out" ||
    code === "interaction_in_progress_cancelled" ||
    code === "access_denied"
  );
}

/**
 * Run on the page the popup lands on, before the app boots. When this window
 * carries a Microsoft sign-in response (`#…state=…&code=…` or `#…&error=…`),
 * hand it to the opener through MSAL's redirect bridge — which also closes the
 * popup — and return `true`: the caller must NOT boot the app here, or it
 * would try to sign someone in inside the popup. Otherwise `false`.
 */
export function finishMicrosoftSignInPopup(): boolean {
  const params = new URLSearchParams(window.location.hash.slice(1));
  if (!params.has("state") || !(params.has("code") || params.has("error"))) return false;
  void import("@azure/msal-browser/redirect-bridge")
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch(() => window.close());
  return true;
}
